import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useToast,
} from '@chakra-ui/react';
import FormRemoteSelectInput from 'components/FormRemoteSelectInput';
import FormWrapper from 'components/FormWrapper';
import InputForm from 'components/InputForm';
import InputNumber from 'components/InputNumber';
import SelectForm from 'components/SelectForm';
import useAuth from 'contexts/useAuth';
import React from 'react';
import { useForm } from 'react-hook-form';
import { createWithdrawal, findAllParticipants } from 'services/api.service';
import { validCnpj, validCpf } from 'services/validDocument';
import { maskCurrency } from 'utils/number';
import { onlyNumbers } from 'utils/string';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import CustomButton from 'components/CustomButton';
import { queryClient } from 'index';

const CreateWithdrawel: React.FC<{
  onClose: () => void;
  asPage?: boolean;
}> = ({ onClose, asPage }) => {
  const toast = useToast();
  const { userData, updateUserData } = useAuth();
  const [loading, setLoading] = React.useState(false);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<{
    ispb: string;
    amount: number;
    account: string;
    description: string;
    agency: string;
    accountHolderDocument: string;
    accountHolderName: string;
    accountType: string;
    dueDate: string;
  }>();

  const { requestTwoFa } = useTwoFaHelper();

  const createwithdrawel = async (data: any) => {
    const code = await requestTwoFa();
    if (!code?.id) {
      return;
    }

    setLoading(true);

    try {
      await createWithdrawal(code.id, {
        ...data,
        accountHolderDocument: data.accountHolderDocument ? onlyNumbers(data.accountHolderDocument) : undefined,
        ispb: data.ispb?.value,
        type: 'MANU',
      });
      updateUserData();
      queryClient.refetchQueries({ queryKey: ['findAllWithdrawers'] });

      toast({
        title: 'Sucesso',
        description: 'Saque criado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao criar saque',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormWrapper
      title="Criar Saque"
      onClose={onClose}
      asPage={asPage}
      footer={
        <CustomButton
          colorScheme="green"
          isLoading={loading}
          onClick={handleSubmit(createwithdrawel)}
        >
          Criar
        </CustomButton>
      }
    >
      <Text fontWeight="semibold">Dados bancários</Text>

      <InputForm
        control={control}
        label="Documento recebedor"
        placeholder="CPF ou CNPJ"
        name="accountHolderDocument"
        type="text"
        rules={{
          required: 'Campo obrigatório',
          validate: {
            valid: (value) => {
              value = onlyNumbers(value);

              return validCpf(value) || validCnpj(value) || 'Documento inválido';
            },
          },
        }}
      />
      <InputForm
        control={control}
        placeholder="Nome do recebedor"
        label="Nome do recebedor"
        name="accountHolderName"
        type="text"
      />

      <FormRemoteSelectInput
        control={control}
        label="Instituição financeira"
        required
        registerOptions={{
          required: 'Esse campo é obrigatório',
        }}
        loadDataFn={({ value, cb }) =>
          findAllParticipants({ filter: value }).then((retorno) => {
            if (retorno?.registers?.length > 0) {
              cb(
                retorno.registers?.map((d: any) => ({
                  label: d.reducedName,
                  value: d.ispb,
                })),
              );
            } else {
              cb([]);
            }
          })
        }
        name="ispb"
      />
      <SelectForm
        control={control}
        label="Tipo de conta"
        name="accountType"
        options={[
          {
            label: 'Conta Corrente',
            value: 'CACC',
          },
          {
            label: 'Conta de Pagamento',
            value: 'TRAN',
          },
          {
            label: 'Conta Poupança',
            value: 'SVGS',
          },
        ]}
      />
      <FormControl isInvalid={!!errors.agency?.message}>
        <FormLabel
          display="flex"
          ms="4px"
          fontSize="sm"
          mb="8px"
        >
          Agência<Text>*</Text>
        </FormLabel>
        <Input
          isRequired={true}
          variant="auth"
          fontSize="sm"
          maxLength={4}
          name="agency"
          {...register('agency', {
            required: 'Esse campo é obrigatório',
            minLength: {
              message: 'Esse campo deve ter no mínimo 4 caracteres',
              value: 4,
            },
            maxLength: {
              message: 'Esse campo deve ter no máximo 4 caracteres',
              value: 4,
            },
          })}
          ms={{ base: '0px', md: '0px' }}
          type="number"
          placeholder="0001"
        />
        <FormErrorMessage>{errors.agency?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.account?.message}>
        <FormLabel
          display="flex"
          ms="4px"
          fontSize="sm"
          mb="8px"
        >
          Conta (com dígito)<Text>*</Text>
        </FormLabel>
        <Input
          isRequired={true}
          variant="auth"
          fontSize="sm"
          name="account"
          {...register('account', {
            required: 'Esse Campo é Obrigatório',
          })}
          ms={{ base: '0px', md: '0px' }}
          type="tel"
          placeholder="00101"
        />
        <FormErrorMessage>{errors.account?.message}</FormErrorMessage>
      </FormControl>

      <InputNumber
        control={control}
        percentage={false}
        placeholder="R$ 0,00"
        label={'Valor'}
        name={`amount`}
        rules={{
          required: 'Campo obrigatório',
          validate: {
            positive: (value) => value > 0 || 'O valor deve ser maior que 0',
            max: (value) =>
              value <= +userData.balance - +userData.amountLocked ||
              'O valor deve ser menor que o saldo disponível',
          },
        }}
      />
      <Text>
        Saldo Disponível: <b>{maskCurrency(userData.balance - +userData.amountLocked)}</b>
      </Text>
      <FormControl isInvalid={!!errors.description?.message}>
        <FormLabel
          display="flex"
          ms="4px"
          fontSize="sm"
          mb="8px"
        >
          Descrição<Text>*</Text>
        </FormLabel>
        <Input
          isRequired={true}
          variant="auth"
          fontSize="sm"
          size="sm"
          {...register('description', {
            maxLength: {
              value: 100,
              message: 'O limite de caracteres é de 100',
            },
          })}
          ms={{ base: '0px', md: '0px' }}
          type="text"
          rounded="sm"
          placeholder="Pagamento de contas"
        />
        <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
      </FormControl>
    </FormWrapper>
  );
};

export default CreateWithdrawel;
